import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash,
  faCopy,
  faCheck,
  faImage,
  faCloudArrowUp
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import './Media.css';

const Media = () => {
  const [images, setImages] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const response = await apiService.getMedia();
      if (response.data.success) {
        setImages(response.data.images || []);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      setImages([]);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const response = await apiService.uploadImage(file);
      // Build a safe URL if backend-provided url is incomplete
      const urlFromApi = response?.data?.url || '';
      const pathFromApi = response?.data?.path || '';
      const fileNameFromApi = response?.data?.filename || '';
      const isValidImageUrl = (u) => {
        if (!u) return false;
        if (u.endsWith('/')) return false;
        return /(\.jpg|\.jpeg|\.png|\.gif|\.webp|\.bmp|\.svg)$/i.test(u);
      };
      const candidatePath = pathFromApi || (fileNameFromApi ? `uploads/images/${fileNameFromApi}` : '');
      const builtUrl = candidatePath ? `${window.location.origin}/storage/${candidatePath}` : '';
      const finalUrl = isValidImageUrl(urlFromApi) ? urlFromApi : builtUrl || urlFromApi;

      const newImage = {
        url: finalUrl,
        path: candidatePath || response.data.path,
        filename: response.data.filename,
        uploaded_at: new Date().toISOString()
      };
      setImages([newImage, ...images]);
      setShowUploader(false);
      return finalUrl;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleDelete = async (image) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await apiService.deleteImage(image.path);
        setImages(images.filter(img => img.path !== image.path));
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete image');
      }
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <div className="media-page">
      <div className="media-header">
        <div>
          <h2>Media Library</h2>
          <p>Upload and manage your images</p>
        </div>
        <button 
          onClick={() => setShowUploader(!showUploader)} 
          className="media-btn media-btn-primary"
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
          Upload Image
        </button>
      </div>

      {showUploader && (
        <div className="media-uploader-section">
          <div className="media-uploader-header">
            <h3>
              <FontAwesomeIcon icon={faCloudArrowUp} style={{ marginRight: '10px', color: '#3b82f6' }} />
              Upload New Image
            </h3>
            <button 
              onClick={() => setShowUploader(false)}
              className="media-close-btn"
            >
              ×
            </button>
          </div>
          <ImageUpload
            value=""
            onChange={() => {}}
            onUpload={handleImageUpload}
          />
        </div>
      )}

      {images.length === 0 ? (
        <div className="media-empty">
          <FontAwesomeIcon icon={faImage} style={{ fontSize: '64px', color: '#cbd5e1', marginBottom: '20px' }} />
          <h3>No images yet</h3>
          <p>Upload your first image to get started</p>
          <button 
            onClick={() => setShowUploader(true)} 
            className="media-btn media-btn-primary"
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
            Upload Image
          </button>
        </div>
      ) : (
        <div className="media-grid">
          {images.map((image, index) => (
            <div key={image.path || index} className="media-item">
              <div className="media-item-image">
                <img 
                  src={image.url} 
                  alt={image.filename} 
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }} 
                />
                <div className="media-item-overlay">
                  <button
                    onClick={() => handleCopyUrl(image.url)}
                    className="media-action-btn media-action-copy"
                    title="Copy URL"
                  >
                    <FontAwesomeIcon icon={copiedUrl === image.url ? faCheck : faCopy} />
                  </button>
                  <button
                    onClick={() => handleDelete(image)}
                    className="media-action-btn media-action-delete"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              <div className="media-item-info">
                <p className="media-item-filename">{image.filename}</p>
                <p className="media-item-url">{image.url}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="media-info-box">
        <h4>📝 Quick Guide</h4>
        <ul>
          <li><strong>Upload:</strong> Click "Upload Image" or drag & drop files</li>
          <li><strong>Copy URL:</strong> Click the copy icon to copy the image URL</li>
          <li><strong>Delete:</strong> Click the trash icon to remove an image</li>
          <li><strong>Use in Posts:</strong> Paste the copied URL in the Featured Image field</li>
        </ul>
      </div>
    </div>
  );
};

export default Media;
